import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  TextField,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import { UserPlus, X, Mail } from 'lucide-react';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { showToast } from '../../store/uiSlice';
import { boardsApi } from '../../api/endpoints/boardsApi';
import { invitationsApi } from '../../api/endpoints/invitationsApi';
import { WorkspaceMember, WorkspaceMemberRole } from '../../types/common';

interface BoardAccessMember {
  id: number;
  fullName: string;
  userName: string;
  email: string;
  role: string;
}

interface BoardAccessTeam {
  id: number;
  name: string;
  members: BoardAccessMember[];
}

interface BoardMembersPanelProps {
  boardId: string;
  workspaceId?: string;
  canManage: boolean;
}

type BoardRoleOption = 'ADMIN' | 'MEMBER' | 'VIEWER';

const ROLE_LABELS: Record<BoardRoleOption, string> = {
  ADMIN: 'Admin',
  MEMBER: 'Editor',
  VIEWER: 'Viewer',
};

// Inverse of the backend's workspaceRoleToBoardRole() mapping - picking
// "Admin" here also grants workspace admin (invitations always carry a
// workspace role too), which is called out in the UI via the helper text
// below the role select.
const BOARD_ROLE_TO_WORKSPACE_ROLE: Record<BoardRoleOption, WorkspaceMemberRole> = {
  ADMIN: WorkspaceMemberRole.ADMIN,
  MEMBER: WorkspaceMemberRole.MEMBER,
  VIEWER: WorkspaceMemberRole.VIEWER,
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const BoardMembersPanel: React.FC<BoardMembersPanelProps> = ({
  boardId,
  workspaceId,
  canManage,
}) => {
  const dispatch = useAppDispatch();
  const { currentWorkspace } = useAppSelector((state) => state.workspaces);

  const [members, setMembers] = useState<BoardAccessMember[]>([]);
  const [teamMembers, setTeamMembers] = useState<BoardAccessMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState<WorkspaceMember | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [role, setRole] = useState<BoardRoleOption>('MEMBER');
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    boardsApi
      .getBoardAccess(boardId)
      .then((access) => {
        setMembers(access.members || []);
        const fromTeams = (access.teams || []).flatMap((team: BoardAccessTeam) => team.members);
        setTeamMembers(fromTeams);
      })
      .catch((error) => {
        console.error('Failed to load project access:', error);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (boardId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId]);

  const directMemberIds = new Set(members.map((m) => String(m.id)));
  // Someone can already have access through a team without a direct
  // BoardMember row - don't offer to "add" them again, but do label them.
  const teamOnlyMembers = teamMembers.filter((m) => !directMemberIds.has(String(m.id)));

  const workspaceMembers = (currentWorkspace?.members || []).filter(Boolean);
  const availableToAdd = useMemo(
    () => workspaceMembers.filter((m) => !directMemberIds.has(String(m.userId))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspaceMembers, members],
  );

  // Typed text that doesn't match any existing/addable workspace member and
  // looks like an email address -> offer to invite them straight onto this
  // board, same as the spec's "Invite New User" action.
  const trimmedInput = inputValue.trim();
  const matchesExistingMember = availableToAdd.some(
    (m) => (m.user?.email || '').toLowerCase() === trimmedInput.toLowerCase(),
  );
  const showInviteOption =
    !selectedMember && !matchesExistingMember && EMAIL_PATTERN.test(trimmedInput);

  const resetPicker = () => {
    setSelectedMember(null);
    setInputValue('');
    setRole('MEMBER');
  };

  const handleAddExisting = async () => {
    if (!selectedMember) return;
    setSubmitting(true);
    try {
      await boardsApi.addMember(boardId, String(selectedMember.userId), role);
      dispatch(showToast({ message: 'Member added to project', type: 'success' }));
      resetPicker();
      load();
    } catch (error: any) {
      const status = error?.response?.status;
      dispatch(
        showToast({
          message:
            status === 409
              ? 'This person already has access to this project'
              : error?.response?.data?.message || error?.message || 'Failed to add member',
          type: 'error',
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleInvite = async () => {
    if (!workspaceId || !trimmedInput) return;
    setSubmitting(true);
    try {
      await invitationsApi.createInvitation(workspaceId, {
        email: trimmedInput,
        role: BOARD_ROLE_TO_WORKSPACE_ROLE[role],
        boardId,
      });
      dispatch(
        showToast({
          message: `Invitation sent to ${trimmedInput}`,
          type: 'success',
        }),
      );
      resetPicker();
    } catch (error: any) {
      const status = error?.response?.status;
      const serverMessage: string = error?.response?.data?.message || '';
      dispatch(
        showToast({
          message:
            status === 409 && /already a member/i.test(serverMessage)
              ? 'This person is already a member of the workspace'
              : serverMessage || error?.message || 'Failed to send invitation',
          type: 'error',
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangeRole = async (userId: number, newRole: BoardRoleOption) => {
    try {
      await boardsApi.updateMemberRole(boardId, String(userId), newRole);
      dispatch(showToast({ message: 'Role updated', type: 'success' }));
      load();
    } catch (error: any) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || error?.message || 'Failed to update role',
          type: 'error',
        }),
      );
    }
  };

  const handleRemove = async (userId: number) => {
    try {
      await boardsApi.removeMember(boardId, String(userId));
      dispatch(showToast({ message: 'Member removed from project', type: 'success' }));
      load();
    } catch (error: any) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || error?.message || 'Failed to remove member',
          type: 'error',
        }),
      );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      {canManage && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Autocomplete
              fullWidth
              size="small"
              options={availableToAdd}
              value={selectedMember}
              inputValue={inputValue}
              onInputChange={(_, value) => setInputValue(value)}
              onChange={(_, value) => setSelectedMember(value)}
              getOptionLabel={(m) => m.user?.name || m.user?.email || `User ${m.userId}`}
              isOptionEqualToValue={(a, b) => a.userId === b.userId}
              noOptionsText={
                EMAIL_PATTERN.test(trimmedInput)
                  ? 'No workspace member found - you can invite this email below'
                  : 'No matching workspace members'
              }
              renderOption={(props, m) => (
                <li {...props} key={m.userId}>
                  <Avatar sx={{ width: 24, height: 24, fontSize: 12, mr: 1 }}>
                    {(m.user?.name || m.user?.email || '?').charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="body2">{m.user?.name || m.user?.email}</Typography>
                    {m.user?.name && (
                      <Typography variant="caption" color="text.secondary">
                        {m.user?.email}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search workspace members or enter an email"
                  placeholder="Name or email..."
                />
              )}
            />
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Role</InputLabel>
              <Select
                label="Role"
                value={role}
                onChange={(e) => setRole(e.target.value as BoardRoleOption)}
              >
                {(Object.keys(ROLE_LABELS) as BoardRoleOption[]).map((r) => (
                  <MenuItem key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              color="primary"
              disabled={!selectedMember || submitting}
              onClick={handleAddExisting}
              aria-label="Add selected member"
              sx={{ mt: 0.5 }}
            >
              <UserPlus size={18} />
            </IconButton>
          </Box>

          {role === 'ADMIN' && (
            <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
              Admin also grants workspace admin access.
            </Typography>
          )}

          {showInviteOption && (
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Mail size={16} />
                <Typography variant="body2" noWrap>
                  "{trimmedInput}" isn't in this workspace yet
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                disabled={submitting || !workspaceId}
                onClick={handleInvite}
              >
                Invite as {ROLE_LABELS[role]}
              </Button>
            </Box>
          )}
        </Box>
      )}

      <List dense>
        {members.map((m) => (
          <ListItem
            key={m.id}
            secondaryAction={
              canManage && (
                <IconButton size="small" onClick={() => handleRemove(m.id)} aria-label="Remove">
                  <X size={16} />
                </IconButton>
              )
            }
          >
            <ListItemAvatar>
              <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                {(m.fullName || m.userName || '?').charAt(0).toUpperCase()}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={m.fullName || m.userName}
              secondary={m.email}
            />
            {canManage ? (
              <Select
                size="small"
                variant="standard"
                value={(m.role as BoardRoleOption) in ROLE_LABELS ? m.role : 'MEMBER'}
                onChange={(e) => handleChangeRole(m.id, e.target.value as BoardRoleOption)}
                sx={{ mr: 1, fontSize: 13 }}
              >
                {(Object.keys(ROLE_LABELS) as BoardRoleOption[]).map((r) => (
                  <MenuItem key={r} value={r} sx={{ fontSize: 13 }}>
                    {ROLE_LABELS[r]}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Chip
                size="small"
                variant="outlined"
                label={ROLE_LABELS[m.role as BoardRoleOption] || m.role}
                sx={{ mr: 1 }}
              />
            )}
          </ListItem>
        ))}

        {teamOnlyMembers.map((m) => (
          <ListItem key={`team-${m.id}`}>
            <ListItemAvatar>
              <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                {(m.fullName || m.userName || '?').charAt(0).toUpperCase()}
              </Avatar>
            </ListItemAvatar>
            <ListItemText primary={m.fullName || m.userName} secondary={m.email} />
            <Chip size="small" label="via team" sx={{ mr: 1 }} />
          </ListItem>
        ))}
      </List>

      {members.length === 0 && teamOnlyMembers.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          No one has been explicitly assigned to this project yet.
        </Typography>
      )}
    </Box>
  );
};

export default BoardMembersPanel;
